import type { ReactNode } from 'react';
import { Children, useEffect, useId, useState } from 'react';

import classNames from 'classnames';

import styles from './Slideshow.module.css';

import { useHtmlElement } from '../../hooks/useHtmlElement';
import { useSlideshow } from '../../hooks/useSlideshow';
import { ArrowButton } from '../ArrowButton';
import { IndexButton } from '../IndexButton';
import { ScrollSnap } from '../ScrollSnap';
import { Slide } from './Slide';
import { isVisible } from './utils/isVisible';

export interface SlideShowProps {
  /** The label used for this slide show. Required for accessibility. */
  label: string;
  /** Boolean value to toggle the display of index dots indicators */
  withDots?: boolean;
  /** Boolean value to toggle next/previous buttons */
  withButtons?: boolean;
  /** Optional class name to pass through to the "next" button */
  nextButtonClass?: string;
  /** Optional class name to pass through to the "previous" button */
  prevButtonClass?: string;
  /** Callback when the current slide changes from a touch gestures */
  onSlideChange?: (nextIndex: number) => void;
  /** The currently active index when used in a controlled state pattern */
  activeIndex?: number;
  /** The scroll behavior when using programmatic scroll */
  scrollBehavior?: ScrollBehavior;
  /** The position for arrow and index buttons */
  controlsPosition?: 'inside' | 'outside';
  /** Toggle looping from last to first and first to last */
  withLoop?: boolean;
  children: ReactNode;
}

const TRANSITION_DURATION = 300;

/**
 * Render a slide show component. This component accepts any number of children
 * that will each be rendered as a slide for the show.
 *
 * !Requires `polyfillSmoothScroll()` on the client for full browser support.
 *
 * @see https://corgi-x.tfd.engineering/components/slideshow
 */
export function Slideshow({
  children,
  label,
  withDots = false,
  withButtons = false,
  nextButtonClass,
  prevButtonClass,
  onSlideChange,
  scrollBehavior: scrollBehaviorProp = 'auto',
  controlsPosition = 'outside',
  activeIndex: activeIndexProp,
  withLoop = false,
}: SlideShowProps) {
  const id = useId();
  const numberOfSlides = Children.count(children);
  const [containerEl, containerRef] = useHtmlElement();
  const [smoothFirstToLast, setSmoothFirstToLast] = useState(false);
  const [smoothLastToFirst, setSmoothLastToFirst] = useState(false);
  const [autoTransition, setAutoTransition] = useState(false);

  const { activeIndex, goToSlide } = useSlideshow({
    scrollableEl: containerEl,
    scrollBehavior: autoTransition ? 'auto' : scrollBehaviorProp,
    getIsActiveSlide: isVisible,
    onSlideChange: onSlideChange,
    activeIndex: activeIndexProp,
  });

  const isLastSlide = activeIndex === numberOfSlides - 1;
  const isFirstSlide = activeIndex === 0;

  const renderLastSlideClone =
    scrollBehaviorProp === 'smooth' && withLoop && smoothFirstToLast;

  const renderFirstSlideClone =
    scrollBehaviorProp === 'smooth' && withLoop && smoothLastToFirst;

  const handlePreviousClick = (): void => {
    // Auto loop transition
    if (scrollBehaviorProp === 'auto' && withLoop && isFirstSlide) {
      goToSlide(numberOfSlides - 1);
      return;
    }

    if (!withLoop && isFirstSlide) {
      return;
    }

    // Smooth loop transition
    if (scrollBehaviorProp === 'smooth' && withLoop && isFirstSlide) {
      // When smoothFirstToLast={true} a clone of the last slide
      // is rendered as the new first slide in the array.
      // The useEffect callback is triggered and will smoothly transition to this cloned slide.
      setSmoothFirstToLast(true);

      // Once smooth transition is complete, useEffect will
      // transition to the actual last slide with no animation.
      setTimeout(() => {
        setAutoTransition(true);
      }, TRANSITION_DURATION);
      return;
    }

    // Regular transition
    goToSlide(activeIndex - 1);
  };

  const handleNextClick = (): void => {
    // Auto loop transition
    if (scrollBehaviorProp === 'auto' && withLoop && isLastSlide) {
      goToSlide(0);
      return;
    }

    if (!withLoop && isLastSlide) {
      return;
    }
    // Smooth loop transition
    if (scrollBehaviorProp === 'smooth' && withLoop && isLastSlide) {
      // When smoothLastToFirst={true} a clone of the first slide
      // is rendered as the new last slide in the array.
      // The useEffect callback is triggered and will smoothly transition to this cloned slide.
      setSmoothLastToFirst(true);

      // Once smooth transition is complete, useEffect will
      // transition to the actual first slide with no animation.
      setTimeout(() => {
        setAutoTransition(true);
      }, TRANSITION_DURATION);
      return;
    }

    // Regular transition
    goToSlide(activeIndex + 1);
  };

  // Callbacks for handlePreviousClick
  useEffect(() => {
    // Smoothly transition to cloned last slide, which is now the first slide in the array.
    if (smoothFirstToLast && activeIndex === 1) {
      goToSlide(0);
    }

    // The handlePreviousClick event handler sets autoTransition={true} after 500ms and
    // once this happens, transition to the actual last slide without an animation.
    if (autoTransition && isFirstSlide) {
      goToSlide(numberOfSlides);
    }

    // Remove cloned last slide & set scrollBehavior back to 'smooth'
    if (autoTransition && smoothFirstToLast && activeIndex === numberOfSlides) {
      setSmoothFirstToLast(false);
      setAutoTransition(false);
    }
  }, [
    smoothFirstToLast,
    isFirstSlide,
    activeIndex,
    goToSlide,
    numberOfSlides,
    autoTransition,
  ]);

  // Callbacks for handleNextClick
  useEffect(() => {
    // Smoothly transition to cloned first slide, which is now the last slide in the array.
    if (smoothLastToFirst && isLastSlide) {
      goToSlide(numberOfSlides);
    }

    // The handleNextClick event handler sets autoTransition={true} after <TRANSITION_DURATION> and
    // once this happens, transition to the actual first slide without an animation.
    if (autoTransition && smoothLastToFirst && activeIndex === numberOfSlides) {
      goToSlide(0);
    }

    // Hide cloned first slide & set scrollBehavior back to 'smooth'
    if (autoTransition && smoothLastToFirst && isFirstSlide) {
      setSmoothLastToFirst(false);
      setAutoTransition(false);
    }
  }, [
    smoothLastToFirst,
    isFirstSlide,
    isLastSlide,
    activeIndex,
    goToSlide,
    numberOfSlides,
    autoTransition,
  ]);

  return (
    <section
      aria-label={label}
      style={{
        position: controlsPosition === 'inside' ? 'relative' : 'static',
      }}
    >
      <div className={styles.container}>
        <ScrollSnap
          as="ul"
          id={id}
          containerRef={containerRef}
          className={styles.horizontalSnap}
        >
          {renderLastSlideClone &&
            Children.map(children, (child, i) => {
              if (i !== numberOfSlides - 1) {
                return <></>;
              }
              return (
                <Slide aria-label={`${numberOfSlides} of ${numberOfSlides}`}>
                  {child}
                </Slide>
              );
            })}
          {Children.map(children, (child, i) => (
            <Slide aria-label={`${i + 1} of ${numberOfSlides}`}>{child}</Slide>
          ))}
          {renderFirstSlideClone &&
            Children.map(children, (child, i) => {
              if (i !== 0) {
                return <></>;
              }
              return (
                <Slide aria-label={`1 of ${numberOfSlides}`}>{child}</Slide>
              );
            })}
        </ScrollSnap>
        {withButtons && (
          <>
            <ArrowButton
              direction="left"
              aria-label="previous"
              aria-controls={id}
              className={classNames(
                styles.previous,
                prevButtonClass,
                styles[controlsPosition]
              )}
              variant={controlsPosition === 'inside' ? 'white-bg' : 'default'}
              onClick={handlePreviousClick}
            />
            <ArrowButton
              direction="right"
              aria-label="next"
              aria-controls={id}
              className={classNames(
                styles.next,
                nextButtonClass,
                styles[controlsPosition]
              )}
              onClick={handleNextClick}
              variant={controlsPosition === 'inside' ? 'white-bg' : 'default'}
            />
          </>
        )}
      </div>
      {withDots && (
        <div className={classNames(styles.dots, styles[controlsPosition])}>
          {Children.map(children, (_, childIndex) => {
            return (
              <IndexButton
                aria-controls={id}
                aria-label={`Go to slide ${childIndex + 1}`}
                active={
                  childIndex === activeIndex &&
                  !smoothFirstToLast &&
                  !smoothLastToFirst
                }
                onClick={(): void => goToSlide(childIndex)}
                variant={controlsPosition === 'inside' ? 'green' : 'dark'}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
